import dayjs from 'dayjs';
import currency from '|/helpers/currency';
import { getStateNameFromAbbreviation } from '|/helpers/us-states';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import { deriveTradeIn, PayoffRow } from './derive-trade-in';

export interface TradeInBenefit {
  id: 'seamless' | 'lower-price' | 'pay-less-upfront' | 'save-more';
  title: string;
  body: string;
}

export interface TradeInDetailsView {
  vehicleTitle: string;
  vehicleYearMake: string;
  vehicleModelTrim: string;
  mileage: string | null;
  imageUrl: string | null;
  offerAmount: number;
  /** Already formatted ("Tue, Dec 22"); null when absent or unparseable. */
  expiresOn: string | null;
  taxSavings: number;
  incentive: number;
  showSavingsBanner: boolean;
  benefits: TradeInBenefit[];
  showPayoff: boolean;
  payoffRows: PayoffRow[];
  payoffBody: string;
}

export interface TradeInDetailsInput {
  summary: ReviewSummaryResponse | null;
  tradeBrand: 'Slate' | 'Carvana';
}

const whole = (value: number) =>
  currency(Math.abs(value), { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const normalized = (value?: string | null) => value?.toLowerCase() ?? '';

const formatExpiry = (value?: string | null) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('ddd, MMM D') : null;
};

/**
 * Two independent switches drive the benefit list. Positive equity shows the lower-price
 * benefit. Negative equity shows the pay-less-upfront benefit only when the amount due at order
 * is less than the trade-in loan balance. Savings pick the last bullet and the banner, which
 * share a visibility rule: no tax saving and no incentive means neither renders.
 */
export const deriveTradeInDetails = ({
  summary,
  tradeBrand
}: TradeInDetailsInput): TradeInDetailsView => {
  const trade = summary?.tradeIn;
  const fundingType = normalized(summary?.funding?.type);
  const isCarvanaFinanced =
    fundingType === 'financing' && normalized(summary?.funding?.institution) === 'carvana';

  const offer = trade?.value ?? 0;
  const lienAmount = trade?.lienAmount ?? 0;
  const tradeDown = summary?.funding?.loan?.details?.tradeDown ?? 0;
  const totalDue = summary?.payment?.dueAtOrder ?? 0;
  const net = offer - lienAmount;
  const isNegativeEquity = net < 0;
  const hasLien = lienAmount > 0;
  // "Rolled into your new Carvana loan" only holds on a Carvana-financed order where the
  // amount due at order is less than the balance still owed on the trade (|net|, not the
  // full lien balance — the offer already covers the rest).
  const isNegativeEquityFinanced =
    isNegativeEquity && isCarvanaFinanced && totalDue < Math.abs(net);

  const taxSavings = summary?.totals?.tradeInTaxSavings ?? 0;
  const incentive = trade?.tradeIncentive ?? 0;
  const hasTaxSavings = taxSavings > 0;
  const hasIncentive = incentive > 0;

  const stateName =
    getStateNameFromAbbreviation(summary?.customer?.address?.state ?? '') ?? 'your state';

  const tradeIn = deriveTradeIn({
    isDeclined: false,
    offer,
    lienAmount,
    tradeDown,
    isNegativeEquityFinanced,
    isTaxSavingsEligible: !!trade?.isTradeInTaxSavingsEligible,
    stateName,
    lienHolder: trade?.lienHolderName ?? null,
    isCarvanaFinanced,
    tradeBrand
  });
  const vehicleYearMake = [trade?.year, trade?.make].filter(Boolean).join(' ');
  const vehicleModelTrim = [trade?.model, trade?.trim].filter(Boolean).join(' ');

  const benefits: TradeInBenefit[] = [
    {
      id: 'seamless',
      title: 'One seamless transaction',
      body: hasLien
        ? "Trade in your car, buy your next one, and we'll handle your loan payoff along the way."
        : 'Trade in your car and apply its value toward your next vehicle.'
    }
  ];

  if (net > 0) {
    benefits.push({
      id: 'lower-price',
      title: 'Lower your purchase price',
      body: `Your ${whole(net)} trade-in value is applied directly to your purchase.`
    });
  } else if (isNegativeEquityFinanced) {
    benefits.push({
      id: 'pay-less-upfront',
      title: 'Pay less of what you owe upfront',
      body: `Part of your trade-in loan balance has been rolled into your new ${tradeBrand} loan.`
    });
  }

  if (hasTaxSavings || hasIncentive) {
    benefits.push({
      id: 'save-more',
      title: 'Save even more',
      body:
        hasTaxSavings && hasIncentive
          ? `You are saving ${whole(taxSavings)} in ${stateName} sales tax and an extra ${whole(
              incentive
            )} from ${tradeBrand} by trading in.`
          : hasTaxSavings
            ? `You are saving ${whole(taxSavings)} in ${stateName} sales tax by trading in.`
            : `You are saving an extra ${whole(incentive)} from ${tradeBrand} by trading in.`
    });
  }

  return {
    vehicleTitle: [vehicleYearMake, vehicleModelTrim].filter(Boolean).join(' '),
    vehicleYearMake,
    vehicleModelTrim,
    mileage:
      trade?.odometer === undefined || trade?.odometer === null
        ? null
        : `${trade.odometer.toLocaleString()} miles`,
    imageUrl: trade?.imageUrl ?? null,
    offerAmount: offer,
    expiresOn: formatExpiry(trade?.expiresOn),
    taxSavings,
    incentive,
    showSavingsBanner: hasTaxSavings || hasIncentive,
    benefits,
    showPayoff: hasLien,
    payoffRows: tradeIn.payoffRows,
    payoffBody: tradeIn.payoffBody
  };
};

export default deriveTradeInDetails;
